"use client";

import { type FC } from "react";
import { ShieldOffIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAnonymizationSendGate } from "@/components/custom/stores/anonymizationSendGate";

/**
 * Send-time gate that fires from the chat adapter (NOT the Send button) when:
 *   1. per-thread `enabled === false` (user explicitly disabled anonymisation), AND
 *   2. `everAnonymized === true` (at least one prior turn was anonymised), AND
 *   3. user has not yet acknowledged for this thread (`acknowledgedUnanonymizedSend`).
 *
 * Adapter-level placement means every send path (button click, Enter key, any
 * programmatic invocation of `composer.send()`) flows through the same check —
 * unlike the earlier ComposerAction-level gate, which the button could only
 * catch on mouse/touch click.
 *
 * Async dispatch: the adapter calls
 * `useAnonymizationSendGate.getState().request(threadId)` and awaits the
 * Promise. This component subscribes to `pending` and renders the dialog when
 * a request is in flight. Confirm/Cancel buttons resolve the Promise with
 * true / false respectively; the adapter then either proceeds with the
 * HTTP POST (and writes the per-thread acknowledgment) or throws an
 * `AbortError` to unwind the run as a user-cancellation.
 *
 * Closing the dialog via the close icon or Escape is treated as Cancel —
 * the next Send attempt re-fires the modal until the user explicitly confirms.
 */
export const AnonymizationSendWarningModal: FC = () => {
  const { t } = useTranslation();
  const pending = useAnonymizationSendGate((s) => s.pending);
  const resolve = useAnonymizationSendGate((s) => s.resolve);

  const open = pending !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resolve(false);
      }}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOffIcon className="size-5 text-amber-500" />
            {t("anonymization.sendWarning.title")}
          </DialogTitle>
          <DialogDescription>
            {t("anonymization.sendWarning.message")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => resolve(false)}>
            {t("anonymization.sendWarning.cancel")}
          </Button>
          <Button variant="destructive" onClick={() => resolve(true)}>
            {t("anonymization.sendWarning.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
